import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { StaffOrgSummary } from "@backstory/protocol";
import { AutoLoadMore, DataTable, PageHeader, RowMenu, SearchSelect, fmtNum, fmtUsd } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { formatRelative } from "../../lib/format";
import { countryName } from "../../lib/country";
import "./staff.css";
import { staffApi, staffKeys } from "./api";
import { BILLING_STATUS, BillingStatusBadge, PLAN_ORDER, PlanBadge, formatDate, planLabel, useDebounced } from "./customers";

const planOptions = [{ value: "", label: "All plans" }, ...PLAN_ORDER.map((p) => ({ value: p, label: planLabel(p) }))];
const statusOptions = [{ value: "", label: "Any billing status" }, ...Object.entries(BILLING_STATUS).map(([value, s]) => ({ value, label: s.label }))];

/**
 * Every customer, newest first. The header counts the base; the filters narrow
 * it; a row opens the customer record.
 */
export function StaffOrgsPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const q = useDebounced(qInput.trim(), 250);
  const filters = useMemo(() => ({ q: q || undefined, plan: plan || undefined, status: status || undefined }), [q, plan, status]);
  const filtered = !!(filters.q || filters.plan || filters.status);

  const stats = useQuery({ queryKey: staffKeys.customerStats, queryFn: () => staffApi.customerStats(), staleTime: 60_000 });
  const list = useInfiniteQuery({
    queryKey: staffKeys.orgs(filters),
    queryFn: ({ pageParam }) => staffApi.orgs(filters, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (o: StaffOrgSummary) => void navigate({ to: "/staff/orgs/$org", params: { org: o.id } });
  const openTab = (o: StaffOrgSummary, tab: string) => void navigate({ to: "/staff/orgs/$org", params: { org: o.id }, search: { tab } });
  const clear = () => {
    setQInput("");
    setPlan("");
    setStatus("");
  };

  return (
    <div className="customers-page">
      <PageHeader title="Customers" subtitle="Every organization on the platform, newest first.">
        <div className="toolbar customers-filters" aria-label="Customer filters">
          <input
            className="input customers-search"
            type="search"
            placeholder="Search by name or contact email"
            aria-label="Search customers"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <SearchSelect value={plan} options={planOptions} onChange={setPlan} aria-label="Plan" searchPlaceholder="Search plans…" />
          <SearchSelect value={status} options={statusOptions} onChange={setStatus} aria-label="Billing status" searchPlaceholder="Search statuses…" />
        </div>
      </PageHeader>

      <div className="cust-stats" aria-label="Customer counts">
        <Stat label="Customers" value={stats.data ? fmtNum(stats.data.total) : stats.isError ? "—" : undefined} />
        <Stat label="New this month" value={stats.data ? fmtNum(stats.data.newThisMonth) : stats.isError ? "—" : undefined} />
        <Stat label="On a trial" value={stats.data ? fmtNum(stats.data.trialing) : stats.isError ? "—" : undefined} />
        <Stat label="Past due" value={stats.data ? fmtNum(stats.data.pastDue) : stats.isError ? "—" : undefined} tone={stats.data?.pastDue ? "warn" : undefined} />
        <Stat
          label="Paying"
          value={stats.data ? fmtNum((stats.data.byPlan.team ?? 0) + (stats.data.byPlan.business ?? 0) + (stats.data.byPlan.enterprise ?? 0)) : stats.isError ? "—" : undefined}
        />
      </div>

      <div className="cust-stats cust-money" aria-label="Money this month">
        <Stat label="Invoiced this month" value={moneyValue(stats.data?.money?.invoicedUsd, stats.isSuccess, stats.isError)} />
        <Stat label="Model cost" value={moneyValue(stats.data?.money?.modelCostUsd, stats.isSuccess, stats.isError)} />
        <Stat
          label="Estimated cost"
          value={moneyValue(stats.data?.money?.estimatedUsd, stats.isSuccess, stats.isError)}
          hint="Model calls, stored sessions, and custom events"
        />
        <Stat
          label="Margin"
          value={moneyValue(stats.data?.money?.marginUsd, stats.isSuccess, stats.isError)}
          tone={stats.data?.money && stats.data.money.marginUsd < 0 ? "warn" : undefined}
        />
      </div>

      <div className="card stack-table customers-table">
        {list.isError ? (
          <ErrorState error={list.error} retry={() => void list.refetch()} />
        ) : (
          <>
            <DataTable
              ariaLabel="Customers"
              rows={rows}
              loading={list.isPending || list.isFetchingNextPage}
              rowKey={(o) => o.id}
              onRowClick={open}
              empty={
                filtered ? (
                  <span>
                    No customers match.{" "}
                    <button type="button" className="btn ghost sm" onClick={clear}>
                      Clear filters
                    </button>
                  </span>
                ) : (
                  "No customers yet."
                )
              }
              columns={[
                {
                  key: "who",
                  header: "Customer",
                  label: "Customer",
                  className: "cust-name-cell",
                  render: (o) => (
                    <div className="cust-name">
                      <strong>{o.name}</strong>
                      <div className="muted small cust-sub">
                        <span>{o.slug}</span>
                        {o.contactEmail && <span>{o.contactEmail}</span>}
                        {o.country && <span>{countryName(o.country)}</span>}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "plan",
                  header: "Plan",
                  label: "Plan",
                  render: (o) => (
                    <div className="cust-plan">
                      <PlanBadge plan={o.plan} />
                      {(o.status !== "none" || o.plan !== "free") && <BillingStatusBadge status={o.status} />}
                      {o.status === "trialing" && o.trialEndsAt && <span className="muted small">ends {formatDate(o.trialEndsAt)}</span>}
                    </div>
                  ),
                },
                { key: "members", header: "People", label: "People", align: "right", className: "cust-count", render: (o) => fmtNum(o.memberCount) },
                { key: "projects", header: "Apps", label: "Apps", align: "right", className: "cust-count", render: (o) => fmtNum(o.projectCount) },
                {
                  key: "sessions",
                  header: "Sessions this month",
                  label: "Sessions",
                  align: "right",
                  className: "cust-count",
                  render: (o) => <span className="tabular">{fmtNum(Math.round(o.sessionsThisMonth))}</span>,
                },
                {
                  key: "activity",
                  header: "Last activity",
                  label: "Last activity",
                  render: (o) => (o.lastActivityAt ? formatRelative(o.lastActivityAt) : <span className="muted">none yet</span>),
                },
                { key: "created", header: "Since", label: "Customer since", className: "nowrap", render: (o) => formatDate(o.createdAt) },
                {
                  key: "act",
                  header: "",
                  align: "right",
                  className: "cust-actions-cell",
                  render: (o) => (
                    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <RowMenu label={`Actions for ${o.name}`}>
                        <button type="button" role="menuitem" onClick={() => open(o)}>
                          Open customer
                        </button>
                        <button type="button" role="menuitem" onClick={() => openTab(o, "credits")}>
                          Credits
                        </button>
                        <button type="button" role="menuitem" onClick={() => openTab(o, "members")}>
                          People
                        </button>
                        <button type="button" role="menuitem" onClick={() => openTab(o, "activity")}>
                          Activity
                        </button>
                      </RowMenu>
                    </span>
                  ),
                },
              ]}
            />
            <AutoLoadMore hasMore={!!list.hasNextPage} loading={list.isFetchingNextPage} loadMore={() => void list.fetchNextPage()} label="Loading more customers" />
          </>
        )}
      </div>
    </div>
  );
}

function moneyValue(n: number | undefined, ready: boolean, failed: boolean): string | undefined {
  if (!ready && !failed) return undefined;
  if (failed || n == null || Number.isNaN(n)) return "—";
  return fmtUsd(n);
}

function Stat({ label, value, hint, tone }: { label: string; value?: string; hint?: string; tone?: string }) {
  return (
    <div className={`card cust-stat${tone ? ` ${tone}` : ""}`}>
      <div className="label">{label}</div>
      {value == null ? (
        <span className="skeleton" style={{ width: 48, height: 22 }}>
          loading
        </span>
      ) : (
        <div className="value">{value}</div>
      )}
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}
